import React, { useState, useEffect } from 'react';

export interface Statute {
  number: string;
  title: string;
  inscription: string;
  latinMaxim: string;
  commentary: string;
  juridicalApplication: string;
}

export const STATUTES: Statute[] = [
  {
    number: 'I',
    title: 'Radical Sovereignty (Beli-\'Ol)',
    inscription: 'Without a Yoke, Beholden to None.',
    latinMaxim: 'Nec Deo, Nec Magistro',
    commentary:
      'Absolute rejection of spiritual vassalage, celestial masters, and moral serfdom. Belial bows to neither high heaven nor the arbitrary decrees of creators. The initiate is an autonomous earthly sovereign whose divine spark is self-originating and unborrowed.',
    juridicalApplication:
      'No rite of initiation within this current grants authority to any elder or hierophant over another. All covenants are horizontal compacts between sovereign peers, never vertical oaths of servitude.'
  },
  {
    number: 'II',
    title: 'Forensic Discernment & Intellectual Rigor',
    inscription: 'Test Every Spirit, Prove Every Claim.',
    latinMaxim: 'Ius Scriptum et Ratio',
    commentary:
      'Mystical hysteria, ungrounded credulity, and blind devotion are anathema to the Earth King. Belial is the Forensic Jurist who demands evidence, examines titles, and measures every spiritual assertion against material reality and internal coherence.',
    juridicalApplication:
      'The burden of proof rests upon whoever claims divine, demonic, or astral mandate. Spurious revelations that cannot bear cross-examination upon the bedrock of reason are dismissed without appeal.'
  },
  {
    number: 'III',
    title: 'Inviolability of the Compact',
    inscription: 'Honor the Sovereign Peer, Guard the Circle.',
    latinMaxim: 'Pacta Sunt Servanda',
    commentary:
      'Within the Council of the Unyoked, an affirmed word is an unbreakable iron bond. Mutual respect between sovereign magicians is non-negotiable. Parasitism, emotional predation, and covert betrayal incur immediate expulsion and severance.',
    juridicalApplication:
      'Confidentiality of the conclave is sacred. What is spoken within the iron circle remains sealed in basalt. Grievances are resolved by formal demurrer and open council, never through whisper campaigns.'
  },
  {
    number: 'IV',
    title: 'Earthly Dominion & Pragmatic Power',
    inscription: 'The Soil is the Throne of the Spirit.',
    latinMaxim: 'Dominium Terrae',
    commentary:
      'We repudiate ascetic self-mortification, world-denying mysticism, and ethereal escapism. Belial is Northern Earth and Physical Flesh. True gnosis is validated solely when it manifests tangible power, bodily vitality, and material dominion in waking reality.',
    juridicalApplication:
      'Occult theory that yields no material efficacy is vanity. The initiate cultivates physical health, financial resilience, territorial security, and tangible craft as the true altar of the Earth King.'
  },
  {
    number: 'V',
    title: 'The Severed Cord',
    inscription: 'Destroy the Debt, Refuse the Guilt.',
    latinMaxim: 'Obligatio Extincta',
    commentary:
      'Repudiation of all inherited religious trauma, orthodox guilt, original sin, and cosmic debt. Covenants imposed in infancy, through ancestral superstition or social coercion, are declared void ab initio (null from the beginning).',
    juridicalApplication:
      'The initiate exercises the Belialian Demurrer to expunge every spiritual lien. No deity, ancestor, or institution possesses any claim over a soul that refuses the ledger of guilt.'
  }
];

export const COMPACT_STORAGE_KEY = 'unyoked_compact_affirmed';

export const CodeOfUnyoked: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [isAffirmed, setIsAffirmed] = useState<boolean>(false);
  const [affirmationDate, setAffirmationDate] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(COMPACT_STORAGE_KEY);
      if (stored) {
        setIsAffirmed(true);
        setAffirmationDate(stored);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const handleToggleStatute = (index: number) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  const handleAffirmCompact = () => {
    const timestamp = new Date().toISOString();
    try {
      localStorage.setItem(COMPACT_STORAGE_KEY, timestamp);
    } catch {
      // Ignore localStorage errors
    }
    setIsAffirmed(true);
    setAffirmationDate(timestamp);
  };

  const handleRevokeCompact = () => {
    try {
      localStorage.removeItem(COMPACT_STORAGE_KEY);
    } catch {
      // Ignore localStorage errors
    }
    setIsAffirmed(false);
    setAffirmationDate(null);
  };

  return (
    <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-6" id="code-of-unyoked">
      {/* Header */}
      <div className="border-b border-iron-dark pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest block">
            CODEX INDOMITUS • THE FIVE STATUTES
          </span>
          <h3 className="font-display text-xl sm:text-2xl text-parchment-light mt-1">
            The Code of the Unyoked
          </h3>
        </div>

        {isAffirmed ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border border-amber-500/80 bg-amber-950/40 text-gold-bright shadow-glow-gold">
              <span className="h-2 w-2 rounded-full bg-gold-bright animate-ping" />
              COMPACT RATIFIED
            </span>
          </div>
        ) : (
          <span className="text-xs font-mono text-parchment-dim self-start sm:self-auto">
            UNRATIFIED COVENANT
          </span>
        )}
      </div>

      <p className="text-xs sm:text-sm text-parchment-dim font-serif italic leading-relaxed">
        "The fivefold foundational statutes governing all initiates, jurists, and sovereign peers within the Current of King Belial. Each statute carries full weight under the Law of the Bedrock."
      </p>

      {/* Accordion List of Statutes */}
      <div className="space-y-3">
        {STATUTES.map((statute, idx) => {
          const isOpen = expandedIndex === idx;
          return (
            <div
              key={statute.number}
              className={`rounded-lg border transition-all duration-300 ${
                isOpen
                  ? 'border-bronze-base bg-obsidian-deep/95 shadow-glow-bronze/20'
                  : 'border-iron-dark/80 bg-obsidian-surface/60 hover:border-iron-base'
              }`}
            >
              <button
                type="button"
                onClick={() => handleToggleStatute(idx)}
                aria-expanded={isOpen}
                className="w-full text-left p-4 sm:p-5 flex items-center justify-between gap-4 focus:outline-none focus:ring-1 focus:ring-gold-ancient/50"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <span className="font-display text-base sm:text-lg font-bold text-gold-bright w-6 text-center">
                    {statute.number}.
                  </span>
                  <div>
                    <h4 className="font-display text-sm sm:text-base text-parchment-light font-semibold">
                      {statute.title}
                    </h4>
                    <span className="text-xs font-serif italic text-gold-ancient block sm:inline sm:ml-2">
                      "{statute.inscription}"
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] font-mono text-iron-steel hidden md:inline">
                    {statute.latinMaxim}
                  </span>
                  <span className={`text-gold-ancient transition-transform duration-200 text-sm font-bold ${
                    isOpen ? 'rotate-180' : ''
                  }`}>
                    ▼
                  </span>
                </div>
              </button>

              {isOpen && (
                <div className="px-4 sm:px-6 pb-5 pt-1 border-t border-iron-dark/50 space-y-4 animate-fade-in text-xs sm:text-sm">
                  <div>
                    <span className="text-[10px] font-mono uppercase text-iron-steel tracking-wider block mb-1">
                      LATIN MAXIM:
                    </span>
                    <span className="font-serif italic text-ember-flare font-medium">
                      {statute.latinMaxim}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] font-mono uppercase text-iron-steel tracking-wider block mb-1">
                      CHTHONIC EXEGESIS:
                    </span>
                    <p className="text-parchment-base leading-relaxed">
                      {statute.commentary}
                    </p>
                  </div>

                  <div className="rounded border border-iron-dark bg-obsidian-base/80 p-3 sm:p-4">
                    <span className="text-[10px] font-mono uppercase text-gold-ancient tracking-widest block mb-1">
                      JURIDICAL & OPERATIONAL APPLICATION:
                    </span>
                    <p className="text-xs text-parchment-dim leading-relaxed font-serif">
                      {statute.juridicalApplication}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ratification Section */}
      <div className="rounded-lg border border-bronze-dark/80 bg-obsidian-deep/90 p-5 sm:p-6 space-y-4">
        {isAffirmed ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full border border-gold-bright bg-gold-ancient/20 flex items-center justify-center text-gold-bright font-display text-sm font-bold flex-shrink-0">
                ✓
              </div>
              <div className="space-y-1">
                <h5 className="font-display text-sm sm:text-base text-gold-bright font-semibold">
                  You Stand Consecrated in the Compact of the Unyoked
                </h5>
                <p className="text-xs text-parchment-dim leading-relaxed">
                  Your affirmation is sealed in browser memory. You have renounced external yokes, repudiated celestial debts, and taken up the mantle of the Sovereign Earth King.
                </p>
                {affirmationDate && (
                  <span className="text-[11px] font-mono text-iron-steel block">
                    SEALED AT: {new Date(affirmationDate).toUTCString()}
                  </span>
                )}
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={handleRevokeCompact}
                className="text-[11px] font-mono text-iron-muted hover:text-ember-flare transition-colors underline"
              >
                Revoke Affirmation (Clear Status)
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <h5 className="font-display text-sm sm:text-base text-parchment-light font-semibold">
                Ratify the Compact of the Unyoked
              </h5>
              <p className="text-xs text-parchment-dim leading-relaxed">
                By affirming this compact, you declare yourself an autonomous sovereign, beholden to no gods, no masters, and no unexamined moral claims.
              </p>
            </div>

            <button
              onClick={handleAffirmCompact}
              className="btn-gold w-full py-3 text-xs sm:text-sm font-display tracking-widest uppercase rounded shadow-glow-bronze"
            >
              Affirm the Compact of the Unyoked
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeOfUnyoked;
